 
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  FaRegStar,
  FaStarHalfAlt,
  FaStar,
  FaCheck,
  FaCheckCircle,
  FaCircle,
  FaCompress,
  FaExpand,
} from 'react-icons/fa';
import { MapContainer, Marker as LeafletMarker, TileLayer, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import theme from 'v3/theme';
import Text from 'v3/components/Text';
import Card from 'v3/components/Card';
import Tooltip from 'v3/components/Tooltip';
import api from 'services/api/';
import { pluralize } from 'v3/utils/formatter';
import SkeletonResume from './Skeleton';
import {
  ContainerStar,
  Image,
  LocationMapContainer,
  ProfileHeader,
  ProfileImageColumn,
  ProfileInfoColumn,
  ProfileTitle,
} from './styles';

const monthNames = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const driverLocationIcon = L.divIcon({
  className: 'driver-location-icon',
  html: '<span></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function formatLocationCapturedAt(value) {
  const date = moment(value);

  if (!date.isValid()) return '';

  return `${date.date()} de ${monthNames[date.month()]} de ${date.year()} às ${date.format(
    'HH:mm',
  )}`;
}

function Resume({ driver }) {
  const mapContainerRef = useRef(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [travelsCount, setTravelsCount] = useState(0);
  const [ratings, setRatings] = useState({
    general: 0,
    schedule: 0,
    app: 0,
  });
  const [selfie, setSelfie] = useState();
  const [verified, setVerified] = useState(false);

  async function fetchRating() {
    const response = await api.get(`driver-rating/${driver.id}`);

    if (response.data) {
      setTravelsCount(response.data.__meta__.travels_count);
      setRatings({
        general: response.data.rating_general,
        app: response.data.rating_app,
        schedule: response.data.rating_schedules,
      });
    }
  }

  function renderStars(rating) {
    const starsQtt = [1, 2, 3, 4, 5];

    return (
      <Tooltip placement="bottom" content={<Text>{Number(rating).toFixed(2)}</Text>}>
        <div>
          {starsQtt.map((star) => {
            if (rating >= star) {
              return <FaStar color="#FFA800" />;
            }

            if (rating < star && rating > star - 1) {
              return <FaStarHalfAlt color="#FFA800" />;
            }

            return <FaRegStar color="#FFA800" />;
          })}
        </div>
      </Tooltip>
    );
  }

  useEffect(() => {
    if (driver && driver.id) {
      fetchRating();

      const { driver: driverSelfie } = driver;
      setSelfie(driverSelfie && driverSelfie.selfie ? driverSelfie.selfie.full_path : null);
    }

    if (driver && driver.driver) {
      setVerified(driver.driver.verified_person);
    }
  }, [driver]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMapFullscreen();
      }
    };

    const handleFullscreenChange = () => {
      setIsMapFullscreen(document.fullscreenElement === mapContainerRef.current);
    };

    if (isMapFullscreen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isMapFullscreen]);

  async function openMapFullscreen() {
    if (mapContainerRef.current?.requestFullscreen) {
      await mapContainerRef.current.requestFullscreen();
      return;
    }

    setIsMapFullscreen(true);
  }

  async function closeMapFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }

    setIsMapFullscreen(false);
  }

  function toggleMapFullscreen() {
    if (isMapFullscreen) {
      closeMapFullscreen();
      return;
    }

    openMapFullscreen();
  }

  const hasLocationValue = (value) => value !== null && value !== undefined && value !== '';
  const latitude = hasLocationValue(driver?.latitude) ? Number(driver.latitude) : null;
  const longitude = hasLocationValue(driver?.longitude) ? Number(driver.longitude) : null;
  const hasValidLocation =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    (latitude !== 0 || longitude !== 0);
  const locationCapturedAt = driver?.location_captured_at
    ? formatLocationCapturedAt(driver.location_captured_at)
    : '';

  return (
    <Row>
      <Col lgs={12} xs={12}>
        <Card loading={!driver} LoadingBodyComponent={() => <SkeletonResume />}>
          <ProfileHeader>
            <ProfileImageColumn>
              <Image
                url={selfie || '/images/user-padrao.png'}
                alt="Imagem de perfil"
              />
            </ProfileImageColumn>
            <ProfileInfoColumn>
              <Row>
                <Col xs={12}>
                  <ProfileTitle>
                    <Text type="subtitle" transform="uppercase" line-height="24px">
                      {driver && driver.social_name}
                    </Text>
                    {verified && (
                      <Tooltip placement="right" content={<Text>Motorista verificado</Text>}>
                        <FaCheck className="mx-3 mb-2" color="#0BB7AF" />
                      </Tooltip>
                    )}
                  </ProfileTitle>
                </Col>
                <Col lg={4} md={4} xs={4}>
                  <Text type="regular" color="gray">
                    {driver && driver.app_uninstalled != null && driver.app_uninstalled <= 2 ? (
                      <FaCheckCircle size={15} color={theme.colors.success} />
                    ) : driver && driver.app_uninstalled > 2 ? (
                      <FaCheckCircle size={15} color={theme.colors.warning} />
                    ) : (
                      <FaCircle size={15} color={theme.colors.danger} />
                    )}
                    {driver && driver.app_uninstalled != null && driver.app_uninstalled <= 2
                      ? ' Ativo no APP'
                      : driver && driver.app_uninstalled > 2
                      ? ' App inativo'
                      : ' Inativo no APP'}
                  </Text>
                </Col>
                <Col lg={4} md={4} xs={4}>
                  <Text type="regular" color="gray">
                    {verified ? (
                      <FaCheckCircle size={15} color={theme.colors.light_blue} />
                    ) : (
                      <FaCircle size={15} color={theme.colors.danger} />
                    )}
                    {verified ? ' Verificado' : ' Não Verificado'}
                  </Text>
                </Col>
                <Col lg={4} md={4} xs={4}>
                  <Text type="regular" color="gray" />
                </Col>
                <Col lg={4} md={4} xs={4} className="my-3">
                  <Text type="body" weight="500">
                    {driver && driver.cgccpf}
                  </Text>
                </Col>
                <Col lg={4} md={4} xs={4} className="my-3">
                  <Text type="body" weight="500">
                    {driver && driver.phone}
                  </Text>
                </Col>
                <Col lg={4} md={4} xs={4} className="my-3 text-center">
                  <Text type="body" weight="500">
                    {travelsCount
                      ? `${travelsCount} ${pluralize('viagem', travelsCount, 'viagens')}`
                      : 'Nenhuma viagem'}
                  </Text>
                </Col>
                <Col xs={12} className="my-3">
                  <hr />
                </Col>
                <Col lg={3} md={3} xs={6} className="d-sm-none d-md-block">
                  <Text type="label" weight="500">
                    CNH
                  </Text>
                  <Text type="body" as="p" weight="500" className="mt-3">
                    {driver && driver.cnh_number}
                  </Text>
                </Col>
                <Col lg={3} md={3} xs={6} className="d-sm-none d-md-block">
                  <Text type="label" weight="500">
                    Tipo
                  </Text>
                  <Text type="body" as="p" weight="500" className="mt-3">
                    {driver && driver.cnh_type}
                  </Text>
                </Col>
                <Col lg={6} md={6} xs={12} className="d-flex justify-content-between ">
                  <ContainerStar>
                    <Text type="label" weight="500" className="d-flex mb-2">
                      Geral
                    </Text>

                    <div>{renderStars(ratings.general)}</div>
                  </ContainerStar>
                  <ContainerStar>
                    <Text type="label" weight="500" className="d-flex mb-2">
                      Horário
                    </Text>

                    <div>{renderStars(ratings.schedule)}</div>
                  </ContainerStar>
                  <ContainerStar>
                    <Text type="label" weight="500" transform="uppercase" className="d-flex mb-2">
                      App
                    </Text>

                    <div>{renderStars(ratings.app)}</div>
                  </ContainerStar>
                </Col>
              </Row>
            </ProfileInfoColumn>
          </ProfileHeader>
          <Col fluid>
            <Row>
              <Col className="mb-2">
                {hasValidLocation && (
                  <LocationMapContainer ref={mapContainerRef} $isFullscreen={isMapFullscreen}>
                    <button
                      type="button"
                      className="driver-map-fullscreen-button"
                      onClick={toggleMapFullscreen}
                      title={isMapFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                      aria-label={isMapFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    >
                      {isMapFullscreen ? <FaCompress /> : <FaExpand />}
                    </button>
                    <MapContainer
                      key={isMapFullscreen ? 'driver-map-fullscreen' : 'driver-map-inline'}
                      center={[latitude, longitude]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <LeafletMarker
                        position={[latitude, longitude]}
                        icon={driverLocationIcon}
                      >
                        {locationCapturedAt && (
                          <LeafletTooltip
                            permanent
                            direction="right"
                            offset={[12, 0]}
                            className="driver-location-tooltip"
                          >
                            {locationCapturedAt}
                          </LeafletTooltip>
                        )}
                      </LeafletMarker>
                    </MapContainer>
                  </LocationMapContainer>
                )}
              </Col>
            </Row>
          </Col>
        </Card>
      </Col>
    </Row>
  );
}

export default Resume;
